import React from "react";
import { useLang } from "../context/LangContext";
import { Activity, Sparkles } from "lucide-react";

const COPY = {
  fr: {
    overline_main: "INDICATIONS · CHAMP D'INTERVENTION",
    title: "Indications fréquemment prises en charge.",
    subtitle: "Nos protocoles de régénération s'adressent à un large spectre de pathologies musculo-squelettiques, ainsi qu'à des applications esthétiques et régénératives. Indications non exhaustives — un diagnostic personnalisé précède toute prise en charge.",
    block_medical: "Indications musculo-squelettiques",
    block_aesthetic: "Applications esthétiques & régénératives",
    medical: [
      "Tendinopathies (tendon d'Achille, tennis elbow, golf elbow, etc.)",
      "Fasciite plantaire",
      "Troubles de la coiffe des rotateurs",
      "Tendinopathie rotulienne (genou du sauteur)",
      "Lombalgies chroniques",
      "Douleurs cervicales et tensions du cou",
      "Douleurs de l'épaule, y compris l'épaule gelée (capsulite rétractile)",
      "Déchirures et lésions musculaires",
      "Douleurs du genou, de la hanche et de la cheville",
      "Symptômes associés à l'arthrose",
      "Syndromes douloureux myofasciaux",
      "Adhérences tissulaires et tissus cicatriciels",
      "Certaines compressions nerveuses, notamment le syndrome du canal carpien",
      "Accompagnement de la récupération après blessure",
      "Accompagnement de la récupération après un effort physique intense",
      "Amélioration de la mobilité et de la fonction articulaire",
      "Soutien de la circulation locale et de l'oxygénation des tissus",
    ],
    aesthetic: [
      "Réduction de l'aspect de la cellulite",
      "Amélioration de la qualité de la peau",
      "Raffermissement cutané du visage et du corps",
      "Protocoles de revitalisation tissulaire",
      "Accompagnement des processus naturels de régénération",
    ],
    disclaimer: "Liste indicative · Une évaluation médicale préalable détermine l'éligibilité de chaque indication.",
  },
  en: {
    overline_main: "INDICATIONS · SCOPE OF CARE",
    title: "Conditions frequently addressed.",
    subtitle: "Our regeneration protocols cover a wide spectrum of musculoskeletal conditions, as well as aesthetic and regenerative applications. Non-exhaustive list — a personalised diagnosis precedes any treatment.",
    block_medical: "Musculoskeletal indications",
    block_aesthetic: "Aesthetic & regenerative applications",
    medical: [
      "Tendinopathies (Achilles tendon, tennis elbow, golf elbow, etc.)",
      "Plantar fasciitis",
      "Rotator cuff disorders",
      "Patellar tendinopathy (jumper's knee)",
      "Chronic lower back pain",
      "Cervical pain and neck tension",
      "Shoulder pain including frozen shoulder (adhesive capsulitis)",
      "Muscle tears and injuries",
      "Knee, hip and ankle pain",
      "Symptoms associated with osteoarthritis",
      "Myofascial pain syndromes",
      "Tissue adhesions and scar tissue",
      "Certain nerve compressions, notably carpal tunnel syndrome",
      "Post-injury recovery support",
      "Recovery support after intense physical effort",
      "Mobility and joint function improvement",
      "Local circulation and tissue oxygenation support",
    ],
    aesthetic: [
      "Reduction of cellulite appearance",
      "Improvement of skin quality",
      "Skin tightening on face and body",
      "Tissue revitalisation protocols",
      "Support of natural regeneration processes",
    ],
    disclaimer: "Indicative list · A prior medical evaluation determines eligibility for each indication.",
  },
  it: {
    overline_main: "INDICAZIONI · AMBITO DI INTERVENTO",
    title: "Indicazioni frequentemente trattate.",
    subtitle: "I nostri protocolli di rigenerazione coprono un ampio spettro di patologie muscolo-scheletriche e applicazioni estetiche e rigenerative. Elenco non esaustivo — una valutazione personalizzata precede ogni trattamento.",
    block_medical: "Indicazioni muscolo-scheletriche",
    block_aesthetic: "Applicazioni estetiche & rigenerative",
    medical: [
      "Tendinopatie (tendine d'Achille, gomito del tennista, gomito del golfista, ecc.)",
      "Fascite plantare",
      "Disturbi della cuffia dei rotatori",
      "Tendinopatia rotulea (ginocchio del saltatore)",
      "Lombalgia cronica",
      "Cervicalgia e tensioni del collo",
      "Dolori alla spalla, inclusa la spalla congelata (capsulite adesiva)",
      "Strappi e lesioni muscolari",
      "Dolori al ginocchio, all'anca e alla caviglia",
      "Sintomi associati all'artrosi",
      "Sindromi dolorose miofasciali",
      "Aderenze tissutali e tessuti cicatriziali",
      "Alcune compressioni nervose, in particolare la sindrome del tunnel carpale",
      "Supporto al recupero post-infortunio",
      "Supporto al recupero dopo sforzo fisico intenso",
      "Miglioramento della mobilità e della funzione articolare",
      "Sostegno della circolazione locale e dell'ossigenazione dei tessuti",
    ],
    aesthetic: [
      "Riduzione dell'aspetto della cellulite",
      "Miglioramento della qualità della pelle",
      "Rassodamento cutaneo del viso e del corpo",
      "Protocolli di rivitalizzazione tissutale",
      "Sostegno dei processi naturali di rigenerazione",
    ],
    disclaimer: "Elenco indicativo · Una valutazione medica preliminare determina l'idoneità di ciascuna indicazione.",
  },
  ru: {
    overline_main: "ПОКАЗАНИЯ · СФЕРА ПРИМЕНЕНИЯ",
    title: "Состояния, с которыми мы регулярно работаем.",
    subtitle: "Наши протоколы регенерации охватывают широкий спектр опорно-двигательных патологий, а также эстетические и регенеративные применения. Неисчерпывающий список — индивидуальная диагностика предшествует любому лечению.",
    block_medical: "Опорно-двигательные показания",
    block_aesthetic: "Эстетика и регенерация",
    medical: [
      "Тендинопатии (ахилл, теннисный/гольфистский локоть и др.)",
      "Подошвенный фасциит",
      "Нарушения вращательной манжеты плеча",
      "Тендинопатия надколенника (колено прыгуна)",
      "Хронические боли в пояснице",
      "Шейные боли и напряжение шеи",
      "Боли в плече, включая замороженное плечо (адгезивный капсулит)",
      "Разрывы и травмы мышц",
      "Боли в колене, бедре и голеностопе",
      "Симптомы, связанные с остеоартрозом",
      "Миофасциальные болевые синдромы",
      "Тканевые спайки и рубцовые ткани",
      "Некоторые нервные компрессии, в частности синдром запястного канала",
      "Сопровождение восстановления после травмы",
      "Сопровождение восстановления после интенсивных нагрузок",
      "Улучшение подвижности и функции суставов",
      "Поддержка локального кровообращения и оксигенации тканей",
    ],
    aesthetic: [
      "Уменьшение проявлений целлюлита",
      "Улучшение качества кожи",
      "Подтяжка кожи лица и тела",
      "Протоколы тканевой ревитализации",
      "Поддержка естественных процессов регенерации",
    ],
    disclaimer: "Ориентировочный список · Медицинская оценка определяет применимость каждого показания.",
  },
};

export default function IndicationsList() {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.en;

  return (
    <section className="container-luxe py-24 md:py-32" data-testid="indications-list">
      <div className="max-w-3xl">
        <div className="overline mb-4">{c.overline_main}</div>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-light text-[var(--ink)] leading-[1.05]">
          {c.title}
        </h2>
        <p className="mt-5 text-[var(--text-secondary)] font-body leading-relaxed">{c.subtitle}</p>
      </div>

      <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Musculoskeletal */}
        <article className="lg:col-span-7 beam-card p-7 md:p-9" data-testid="indications-medical">
          <div className="flex items-center gap-3">
            <Activity size={20} className="text-[var(--gold)]" />
            <span className="overline">{c.block_medical}</span>
          </div>
          <ul className="mt-7 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
            {c.medical.map((item) => (
              <li key={item} className="text-[14px] text-[var(--ink)]/85 font-body leading-relaxed flex items-start gap-3">
                <span className="text-[var(--gold)] mt-[10px] w-3 h-px bg-[var(--gold)] shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </article>

        {/* Aesthetic */}
        <article className="lg:col-span-5 beam-card p-7 md:p-9" data-testid="indications-aesthetic">
          <div className="flex items-center gap-3">
            <Sparkles size={20} className="text-[var(--gold)]" />
            <span className="overline">{c.block_aesthetic}</span>
          </div>
          <ul className="mt-7 space-y-3">
            {c.aesthetic.map((item) => (
              <li key={item} className="text-[14px] text-[var(--ink)]/85 font-body leading-relaxed flex items-start gap-3">
                <span className="text-[var(--gold)] mt-[10px] w-3 h-px bg-[var(--gold)] shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </article>
      </div>

      <p className="mt-10 text-[11px] uppercase tracking-[0.2em] text-[var(--ink)]/40 font-display">
        {c.disclaimer}
      </p>
    </section>
  );
}
